import config from 'config'
import React from 'react'

import { Cover, Stack } from 'design-system'

import BaseLayout from 'src/layouts/base'
import LegacyEidHelp from 'src/components/legacyEidHelp'
import { ArchivedBoardView } from 'src/components/board/archived/view'
import { ContractExpiredView } from 'src/components/network/contract-expired/view'
import { BlockedFromBoard } from 'src/components/board/blocked-from-board/blocked-from-board'
import { TimeoutError } from 'src/components/timeout-error'

const interceptMsg = {
  'bad-query': (
    <div>
      <h4>Bad Query</h4>
    </div>
  ),
  'query-failed': (
    <div>
      <h4>Query Failed</h4>
      <div className="p-2">
        <p>Something went wrong when trying to retrieve your information</p>
        <p>Please wait a minute and try again</p>
      </div>
    </div>
  ),
  'query-timeout': (
    <div>
      <h4>Request Timed Out</h4>
      <div className="p-2">
        <p>Loading this page took longer than expected</p>
        <p>Please wait a minute and try again</p>
      </div>
    </div>
  ),
  'request-timeout': (
    <div>
      <h4>Request Timed Out</h4>
      <div className="p-2">
        <p>Loading this page took longer than expected</p>
        <p>Please wait a minute and try again</p>
      </div>
    </div>
  ),
  'connection-refused': (
    <div>
      <h4>{"Can't Connect"}</h4>
      <div className="p-2">
        <p>There was a problem fetching data to load this page</p>
      </div>
    </div>
  ),
  'invalid-network': (
    <div>
      <p>You are not a member of this organization</p>
      <p>Contact an administrator to request an invite</p>
    </div>
  ),
  'not-a-network': (
    <div>
      <p>{"Couldn't find that organization"}</p>
    </div>
  ),
  'not-allowed': (
    <div>
      <h3>Not Allowed</h3>
      <p>You are not allowed to view this page</p>
    </div>
  ),
  'not-found': (
    <div>
      <h3>Page Not Found</h3>
    </div>
  ),
  'insights-access-denied': (
    <div>
      <h3>Insights access unavailable</h3>
      <p>
        Your account does not have access to Insights. Access is available only
        to network administrators, network owners, and board members with the
        required permissions. Please contact your Success Manager or{' '}
        <a href="mailto:support@yellowdig.com">support@yellowdig.com</a> for
        help.
      </p>
    </div>
  ),

  'is-legacy-eid': <LegacyEidHelp />,
  'gql-error': (
    <div>
      <h3>Error Fetching Data</h3>
      <p>
        We failed to fetch the neccessary data to load this page, please contact
        support.
      </p>
    </div>
  ),
  'gql-client-error': (
    <div>
      <h3>Client Error: Failed To Fetch Data</h3>
      <p>
        We failed to fetch the neccessary data to load this page, please contact
        support.
      </p>
    </div>
  ),
  'invalid-share-link-token': (
    <p>
      Looks like this link is invalid or disabled. Please reach out to the
      original sharer.
    </p>
  ),
  'network-contract-expired': (
    <p>
      Your institution’s Yellowdig access has ended, so your communities are
      paused for now. Your institution administrators have been notified.
    </p>
  ),
  // Clojure keyword error codes are serialized with a leading colon
  ':network-not-found': (
    <p>The organization you are trying to access does not exist.</p>
  )
}

const genericMessage = `Something went wrong`
const errorsWithEmbeddedSupport = new Set(['insights-access-denied'])

const InterceptPage = props => {
  const { error = {}, data = {} } = props

  const msg = interceptMsg[error] || error[':yd-anom/message'] || genericMessage

  if (error === 'board-is-archived') {
    return <ArchivedBoardView />
  }

  if (error === 'contract-expired' || error === ':contract-expired') {
    return <ContractExpiredView role={data.role} />
  }

  if (error === 'user-blocked-from-board') {
    return <BlockedFromBoard />
  }

  if (error === 'gql-request-timeout') {
    return (
      <Cover fullSize className="h-screen">
        <TimeoutError />
      </Cover>
    )
  }

  return (
    <Cover fullSize className="h-screen">
      <Stack
        className="w-11/12 overflow-y-auto rounded-lg bg-white p-6 text-center lg:w-1/2"
        gap={2}>
        <div>{msg}</div>
        {!errorsWithEmbeddedSupport.has(error) && (
          <p>
            Contact{' '}
            <a href="mailto:support@yellowdig.com">support@yellowdig.com</a> for
            additional help
          </p>
        )}
        <div>
          <a href={config.NEXT_PUBLIC_YD_URL}>Go Home</a>
        </div>
      </Stack>
    </Cover>
  )
}

InterceptPage.getLayout = page => (
  <BaseLayout
    header={false}
    outerClassName="bg-gradient-to-tl from-orange-100 to-blue-100"
    title="Yellowdig">
    {page}
  </BaseLayout>
)

export default InterceptPage
